import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

import ButtonIcon from '../../../../common/ButtonIcon/ButtonIcon';
import VocabularyButton from '../../../../common/VocabularyButton/VocabularyButton';
import IconTooltip from '../../../../common/IconTooltip/IconTooltip';
import MaterialTiles from '../../Programs/ProgramsCard/MaterialTiles';
import usePreviewFeature from '../../../../hooks/usePreviewFeature';

import './program-item.style.scss';
import '../../Programs/ProgramsCard/course-card.style.scss';
import { homeworkColors } from '../../../schedule/constants';

const CourseProgramItem = ({ item }) => {
  const isRedesign = usePreviewFeature('courses-redesign');
  const isLessonVideo = usePreviewFeature('lesson-telegram-video');
  const location = useLocation();
  const navigate = useNavigate();

  const borderColor = (status) => {
    if (status === 1) return 'br-green';
    if (status === 2) return 'br-yellow';
    if (status === 3) return 'br-red';

    return '';
  };

  const setLink = (link) => {
    if (!link) return;
    window.open(link, '_blank');
  };

  const setHomeworkPage = (event) => {
    localStorage.setItem('event', JSON.stringify(event));
    localStorage.setItem('eventId', JSON.stringify(event.event_id));
    localStorage.setItem('realHomeworkId', JSON.stringify(event.homework_id));

    navigate('/homework', { state: { from: location.pathname } });
  };

  const homeworkColorIdx = Number.isInteger(item?.homework_icon_color)
    ? item.homework_icon_color
    : 4;

  const shouldPulse = homeworkColorIdx !== 4;
  const hex = homeworkColors?.[homeworkColorIdx];
  const pulseStyle = shouldPulse && hex ? { '--pulse-color': hex } : {};

  const hasMaterials =
    item.presentation_link ||
    item.google_pdf_url ||
    item.vocabulary_link ||
    item.video_url ||
    item.is_show_homework ||
    (isLessonVideo && item.group_video_count > 0);

  if (isRedesign) {
    const status = Number(item.attendance_status) || 0;
    const stateClass = {
      1: 'program-item--was',
      2: 'program-item--part',
      3: 'program-item--miss',
      4: 'program-item--miss',
    }[status] || '';

    return (
      <div className={`program-item program-item--rd ${status > 0 ? 'program-item--done' : ''} ${stateClass}`.trim()}>
        <div className="program-item-content">
          <div className={`program-item__count ${borderColor(item.attendance_status)}`}>
            {item.lesson_number}
          </div>

          <div className="program-item__body">
            <div className="program-item__details">
              <h3 className="program-item__title">{item.topic || '-'}</h3>
              <span className="program-item__subtitle">{item.date || '-'}</span>
            </div>

            {hasMaterials || item.vocabulary_token ? (
              <MaterialTiles lesson={item} />
            ) : (
              item.is_show_next_record_materials_notice && (
                <div className="program-item__notice program-item__notice--inline">
                  {item.next_record_materials_notice_text}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="program-item">
      <div className="program-item-content">
        <div className={`program-item__count ${borderColor(item.attendance_status)}`}>
          {item.lesson_number}
        </div>

        <div className="program-item__body">
          <div className="program-item__details">
            <h3 className="program-item__title">{item.topic || '-'}</h3>
            <h3 className="program-item__subtitle">{item.date || '-'}</h3>
          </div>

          <div className="program-item__btns">
            {hasMaterials ? (
              <>
                {item.presentation_link && (
                  <IconTooltip icon="monitor">
                    <ButtonIcon
                      classes="program-item__btn"
                      icon="monitor"
                      btnBg="white"
                      iconColor="green"
                      btnSize="lg"
                      iconSize="md"
                      onClick={() => setLink(item.presentation_link)}
                    />
                  </IconTooltip>
                )}

                {item.google_pdf_url && (
                  <IconTooltip icon="pdf">
                    <ButtonIcon
                      classes="program-item__btn"
                      icon="pdf"
                      btnBg="white"
                      iconColor="green"
                      btnSize="lg"
                      iconSize="md"
                      onClick={() => setLink(item.google_pdf_url)}
                    />
                  </IconTooltip>
                )}

                {item.vocabulary_link && (
                  <IconTooltip icon="list">
                    <ButtonIcon
                      classes="program-item__btn"
                      icon="list"
                      btnBg="white"
                      iconColor="green"
                      btnSize="lg"
                      iconSize="md"
                      onClick={() => setLink(item.vocabulary_link)}
                    />
                  </IconTooltip>
                )}

                {item.vocabulary_token && (
                  <IconTooltip text="Слова з озвучкою">
                    <VocabularyButton classes="program-item__btn" token={item.vocabulary_token} />
                  </IconTooltip>
                )}

                {item.video_url && (
                  <IconTooltip icon="video">
                    <ButtonIcon
                      classes="program-item__btn"
                      icon="video"
                      btnBg="white"
                      iconColor="green"
                      btnSize="lg"
                      iconSize="md"
                      onClick={() => setLink(item.video_url)}
                    />
                  </IconTooltip>
                )}

                {item.is_show_homework && (
                  <IconTooltip icon="book" homeworkColorIdx={homeworkColorIdx}>
                    <div className="homework-icon-wrapper">
                      {item.is_show_icon_new && (
                        <div className="new-badge">new</div>
                      )}

                      <ButtonIcon
                        classes={`program-item__btn ${shouldPulse ? 'pulsing-icon' : ''}`}
                        icon="book"
                        btnBg="white"
                        iconColor={homeworkColors?.[homeworkColorIdx]}
                        btnSize="lg"
                        iconSize="md"
                        onClick={() => setHomeworkPage(item)}
                        style={pulseStyle}
                      />
                    </div>
                  </IconTooltip>
                )}
              </>
            ) : (
              item.is_show_next_record_materials_notice && (
                <div className="program-item__notice program-item__notice--inline">
                  {item.next_record_materials_notice_text}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

CourseProgramItem.propTypes = {
  item: PropTypes.object.isRequired,
};

export default CourseProgramItem;